<template>
  <div>
    <KTCodePreview :title="titlePage">
      <template v-slot:preview>
        <div>
          <b-card class="mb-5" :class="bannerClass">
            <b-row align-v="center">
              <b-col cols="12" md="8">
                <h4 class="mb-2">
                  Đồng bộ tất cả sản phẩm sang CRM
                  <span
                    v-if="status.status"
                    :class="`label label-inline label-pill label-rounded font-weight-bold ml-2 ${statusLabelClass}`"
                  >{{ statusLabel }}</span>
                </h4>
                <div v-if="status.runId">
                  <div>
                    <b>Lần chạy gần nhất:</b> #{{ status.runId }}
                    <span v-if="status.startedAt">
                      &nbsp;·&nbsp;Bắt đầu: {{ formatDate(status.startedAt) }}
                    </span>
                    <span v-if="status.finishedAt">
                      &nbsp;·&nbsp;Kết thúc: {{ formatDate(status.finishedAt) }}
                      &nbsp;·&nbsp;Thời gian: {{ formatDuration(status.startedAt, status.finishedAt) }}
                    </span>
                  </div>
                  <div class="mt-1">
                    <b>Số nhóm:</b> {{ status.totalBatches ?? '—' }}
                    &nbsp;·&nbsp;<b>Sản phẩm:</b> {{ status.totalParents ?? status.estimatedParents ?? '—' }}
                    &nbsp;·&nbsp;<b>Phiên bản:</b> {{ status.totalChildren ?? '—' }}
                    &nbsp;·&nbsp;<b>Thất bại:</b>
                    <span :class="status.failedBatchesCount > 0 ? 'text-danger font-weight-bold' : ''">
                      {{ status.failedBatchesCount || 0 }}
                    </span>
                  </div>
                  <div v-if="status.error" class="mt-1 text-danger">
                    <b>Lỗi:</b> {{ status.error }}
                  </div>
                </div>
                <div v-else class="text-muted">
                  Chưa có lần đồng bộ nào.
                </div>
              </b-col>
              <b-col cols="12" md="4" class="text-md-right text-left mt-3 mt-md-0">
                <b-form-select
                  v-model="batchSize"
                  :options="batchSizeOptions"
                  size="sm"
                  class="d-inline-block mr-2"
                  style="width: 140px"
                ></b-form-select>
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="status.running || isStarting"
                  @click="onStartSync"
                >
                  <i class="fa-solid fa fa-sync mr-1"></i>
                  <span v-if="status.running">Đang chạy…</span>
                  <span v-else>Đồng bộ ngay</span>
                </b-button>
                <b-button
                  size="sm"
                  variant="secondary"
                  class="ml-2"
                  @click="loadAll"
                >
                  <i class="fa fa-redo"></i>
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          <b-tabs v-model="activeTab" content-class="mt-4">
            <b-tab title="Lịch sử đồng bộ toàn bộ">
              <b-table
                :items="runs.data"
                :fields="runFields"
                bordered
                hover
                show-empty
                empty-text="Không có lần chạy nào."
              >
                <template #cell(id)="row">#{{ row.item.id }}</template>
                <template #cell(startedAt)="row">{{ formatDate(row.item.startedAt) }}</template>
                <template #cell(duration)="row">{{ formatDuration(row.item.startedAt, row.item.finishedAt) }}</template>
                <template #cell(status)="row">
                  <span
                    :class="`label label-inline label-pill label-rounded font-weight-bold ${runStatusClass(row.item.status)}`"
                  >{{ runStatusLabel(row.item.status) }}</span>
                </template>
                <template #cell(failedBatchesCount)="row">
                  <span :class="row.item.failedBatchesCount > 0 ? 'text-danger font-weight-bold' : ''">
                    {{ row.item.failedBatchesCount }}
                  </span>
                </template>
                <template #cell(action)="row">
                  <b-button
                    size="sm"
                    variant="link"
                    class="text-primary font-weight-bold p-0"
                    @click="toggleBatches(row.item)"
                  >
                    {{ expandedRunId === row.item.id ? 'Ẩn' : 'Chi tiết' }}
                  </b-button>
                </template>
                <template #row-details="row">
                  <div v-if="expandedRunId === row.item.id">
                    <div v-if="batchesLoading" class="text-center py-3">
                      <b-spinner small></b-spinner> Đang tải…
                    </div>
                    <b-table
                      v-else
                      :items="batchesForExpanded"
                      :fields="batchFields"
                      small
                      bordered
                      show-empty
                      empty-text="Không có nhóm nào."
                    >
                      <template #cell(status)="b">
                        <span
                          :class="`label label-inline label-pill label-rounded font-weight-bold ${b.item.status === 'ok' ? 'label-success' : 'label-danger'}`"
                        >{{ b.item.status }}</span>
                      </template>
                      <template #cell(sentAt)="b">{{ formatDate(b.item.sentAt) }}</template>
                      <template #cell(parentIds)="b">
                        <span class="text-monospace small">{{ summarizeIds(b.item.parentIds) }}</span>
                      </template>
                      <template #cell(error)="b">
                        <pre v-if="b.item.error" class="m-0 small">{{ b.item.error }}</pre>
                      </template>
                      <template #cell(action)="b">
                        <b-button
                          size="sm"
                          variant="link"
                          class="text-primary font-weight-bold p-0"
                          @click="openBatchDetail(b.item)"
                        >Xem</b-button>
                      </template>
                    </b-table>
                  </div>
                </template>
              </b-table>
              <b-row v-if="runs.total > runsPerPage" align-v="center" class="mt-2">
                <b-col>
                  <span class="text-muted">Tổng: {{ runs.total }}</span>
                </b-col>
                <b-col cols="auto">
                  <b-pagination
                    v-model="runsPage"
                    :total-rows="runs.total"
                    :per-page="runsPerPage"
                    size="sm"
                    align="right"
                    class="mb-0"
                    @input="refreshRuns"
                  ></b-pagination>
                </b-col>
              </b-row>
            </b-tab>

            <b-tab
              :title="`Lỗi đồng bộ theo thao tác${mutationFailures.total ? ` (${mutationFailures.total})` : ''}`"
              @click="ensureMutationFailuresLoaded"
            >
              <b-table
                :items="mutationFailures.data"
                :fields="mutationFields"
                bordered
                hover
                show-empty
                empty-text="Không có lỗi nào."
              >
                <template #cell(sentAt)="row">{{ formatDate(row.item.sentAt) }}</template>
                <template #cell(parentIds)="row">
                  <span class="text-monospace small">{{ summarizeIds(row.item.parentIds) }}</span>
                </template>
                <template #cell(httpStatus)="row">{{ row.item.httpStatus ?? '—' }}</template>
                <template #cell(error)="row">
                  <pre v-if="row.item.error" class="m-0 small">{{ truncate(row.item.error, 200) }}</pre>
                </template>
                <template #cell(action)="row">
                  <b-button size="sm" variant="link"
                    class="text-primary font-weight-bold p-0" @click="openBatchDetail(row.item)">Xem</b-button>
                </template>
              </b-table>
              <b-row v-if="mutationFailures.total > failuresPerPage" align-v="center" class="mt-2">
                <b-col>
                  <span class="text-muted">Tổng: {{ mutationFailures.total }}</span>
                </b-col>
                <b-col cols="auto">
                  <b-pagination
                    v-model="failuresPage"
                    :total-rows="mutationFailures.total"
                    :per-page="failuresPerPage"
                    size="sm"
                    align="right"
                    class="mb-0"
                    @input="refreshMutationFailures"
                  ></b-pagination>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>
        </div>

        <b-modal
          ref="batch-detail-modal"
          size="lg"
          hide-footer
          :title="batchDetail ? `Chi tiết nhóm ${batchDetail.batchIndex != null ? '#' + batchDetail.batchIndex : '(theo thao tác)'}` : ''"
        >
          <div v-if="batchDetail">
            <b-row class="mb-3">
              <b-col cols="6">
                <b>Trạng thái:</b>
                <span
                  :class="`label label-inline label-pill label-rounded font-weight-bold ml-1 ${batchDetail.status === 'ok' ? 'label-success' : 'label-danger'}`"
                >{{ batchDetail.status }}</span>
              </b-col>
              <b-col cols="6"><b>HTTP:</b> {{ batchDetail.httpStatus ?? '—' }}</b-col>
              <b-col cols="6"><b>Gửi lúc:</b> {{ formatDate(batchDetail.sentAt) }}</b-col>
              <b-col cols="6"><b>Thời gian:</b> {{ batchDetail.tookMs != null ? batchDetail.tookMs + 'ms' : '—' }}</b-col>
              <b-col cols="6"><b>Số sản phẩm:</b> {{ batchDetail.parentCount }}</b-col>
              <b-col cols="6" v-if="batchDetail.runId"><b>Run:</b> #{{ batchDetail.runId }}</b-col>
            </b-row>

            <div v-if="batchDetail.error" class="mb-3">
              <b>Lỗi:</b>
              <pre class="m-0 mt-1 small">{{ batchDetail.error }}</pre>
            </div>

            <div>
              <div class="d-flex align-items-center mb-2">
                <b>Danh sách ID sản phẩm ({{ (batchDetail.parentIds || []).length }}):</b>
                <b-button
                  size="sm"
                  variant="link"
                  class="text-primary font-weight-bold ml-2 p-0"
                  @click="copyIds(batchDetail.parentIds)"
                >
                  <i class="far fa-copy"></i> Copy
                </b-button>
              </div>
              <pre class="m-0 small ids-pre">{{ (batchDetail.parentIds || []).join('\n') }}</pre>
            </div>
          </div>
        </b-modal>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { formatDate, makeToastFaile, makeToastSuccess, copyClipBoard } from '@/utils/common';

const KTCodePreview = () =>
  import(/* webpackPrefetch: true */ '@/view/content/CodePreview.vue');

const POLL_INTERVAL_MS = 3000;

export default {
  components: { KTCodePreview },
  data() {
    return {
      status: {
        runId: null,
        running: false,
        startedAt: null,
        finishedAt: null,
        batchSize: null,
        estimatedParents: null,
        totalParents: null,
        totalChildren: null,
        totalBatches: null,
        failedBatchesCount: 0,
        status: null,
        error: null,
      },
      runs: { data: [], total: 0 },
      runsPage: 1,
      runsPerPage: 10,
      failuresPage: 1,
      failuresPerPage: 10,
      runFields: [
        { key: 'id', label: 'ID' },
        { key: 'startedAt', label: 'Bắt đầu' },
        { key: 'duration', label: 'Thời gian' },
        { key: 'batchSize', label: 'Lô' },
        { key: 'totalParents', label: 'Sản phẩm' },
        { key: 'totalBatches', label: 'Nhóm' },
        { key: 'failedBatchesCount', label: 'Thất bại' },
        { key: 'status', label: 'Trạng thái' },
        { key: 'action', label: '', class: 'text-right' },
      ],
      batchFields: [
        { key: 'batchIndex', label: '#' },
        { key: 'parentCount', label: 'Số SP' },
        { key: 'parentIds', label: 'IDs' },
        { key: 'status', label: 'Trạng thái' },
        { key: 'httpStatus', label: 'HTTP' },
        { key: 'tookMs', label: 'ms' },
        { key: 'sentAt', label: 'Gửi lúc' },
        { key: 'error', label: 'Lỗi' },
        { key: 'action', label: '', class: 'text-right' },
      ],
      mutationFields: [
        { key: 'id', label: 'ID' },
        { key: 'sentAt', label: 'Thời gian' },
        { key: 'parentCount', label: 'Số SP' },
        { key: 'parentIds', label: 'IDs' },
        { key: 'httpStatus', label: 'HTTP' },
        { key: 'error', label: 'Lỗi' },
        { key: 'action', label: '', class: 'text-right' },
      ],
      mutationFailures: { data: [], total: 0, loaded: false },
      batchSize: 100,
      batchSizeOptions: [
        { value: 50, text: 'Lô 50' },
        { value: 100, text: 'Lô 100' },
        { value: 200, text: 'Lô 200' },
        { value: 500, text: 'Lô 500' },
      ],
      isStarting: false,
      expandedRunId: null,
      batchesForExpanded: [],
      batchesLoading: false,
      pollTimer: null,
      activeTab: 0,
      batchDetail: null,
    };
  },
  computed: {
    titlePage() {
      return 'Đồng bộ sản phẩm sang CRM';
    },
    statusLabel() {
      return { running: 'Đang chạy', done: 'Hoàn tất', crashed: 'Lỗi' }[this.status.status] || '';
    },
    statusLabelClass() {
      return {
        running: 'label-primary',
        done: 'label-success',
        crashed: 'label-danger',
      }[this.status.status] || 'label-secondary';
    },
    bannerClass() {
      return this.status.running ? 'border-primary' : '';
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Sản phẩm', route: '/products' },
      { title: 'Đồng bộ sang CRM' },
    ]);
    this.loadAll();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    formatDate,
    makeToastFaile,

    async loadAll() {
      await Promise.all([this.refreshStatus(), this.refreshRuns()]);
      this.ensurePolling();
    },

    async refreshStatus() {
      try {
        const res = await this.$api.crmSync.getStatus();
        this.status = res.data.data || this.status;
      } catch (err) {
        this.makeToastFaile(err);
      }
    },

    async refreshRuns() {
      try {
        const offset = (this.runsPage - 1) * this.runsPerPage;
        const res = await this.$api.crmSync.listRuns({ limit: this.runsPerPage, offset });
        const payload = res.data.data;
        const data = Array.isArray(payload) ? payload : (payload?.data || []);
        const total = Array.isArray(payload) ? data.length : (payload?.total ?? data.length);
        this.runs = { data, total };
        for (const r of this.runs.data) {
          if (this.expandedRunId === r.id) r._showDetails = true;
        }
      } catch (err) {
        this.makeToastFaile(err);
      }
    },

    async ensureMutationFailuresLoaded() {
      if (this.mutationFailures.loaded) return;
      await this.refreshMutationFailures();
    },

    async refreshMutationFailures() {
      try {
        const offset = (this.failuresPage - 1) * this.failuresPerPage;
        const res = await this.$api.crmSync.listMutationFailures({
          limit: this.failuresPerPage,
          offset,
        });
        const payload = res.data.data;
        this.mutationFailures = {
          data: payload?.data || [],
          total: payload?.total || 0,
          loaded: true,
        };
      } catch (err) {
        this.makeToastFaile(err);
      }
    },

    ensurePolling() {
      if (this.status.running && !this.pollTimer) {
        this.pollTimer = setInterval(() => this.pollTick(), POLL_INTERVAL_MS);
      } else if (!this.status.running && this.pollTimer) {
        this.stopPolling();
      }
    },

    async pollTick() {
      await this.refreshStatus();
      if (!this.status.running) {
        this.stopPolling();
        await this.refreshRuns();
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async onStartSync() {
      if (this.isStarting || this.status.running) return;
      this.isStarting = true;
      try {
        const res = await this.$api.crmSync.startSync(this.batchSize);
        const data = res.data.data;
        if (data?.status === 'started') {
          makeToastSuccess(`Đã bắt đầu đồng bộ runId=${data.runId}`);
        } else if (data?.status === 'already-running') {
          makeToastSuccess(`Một lần đồng bộ đang chạy (runId=${data.runId})`);
        } else if (data?.status === 'disabled') {
          this.makeToastFaile('CRM webhook chưa được cấu hình.');
        }
        this.runsPage = 1;
        await this.loadAll();
      } catch (err) {
        this.makeToastFaile(err);
      } finally {
        this.isStarting = false;
      }
    },

    async toggleBatches(run) {
      if (this.expandedRunId === run.id) {
        this.expandedRunId = null;
        run._showDetails = false;
        return;
      }
      for (const r of this.runs.data) r._showDetails = false;
      this.expandedRunId = run.id;
      run._showDetails = true;
      this.batchesLoading = true;
      this.batchesForExpanded = [];
      try {
        const res = await this.$api.crmSync.listBatches(run.id);
        this.batchesForExpanded = res.data.data || [];
      } catch (err) {
        this.makeToastFaile(err);
      } finally {
        this.batchesLoading = false;
      }
    },

    openBatchDetail(batch) {
      this.batchDetail = batch;
      this.$refs['batch-detail-modal'].show();
    },

    copyIds(ids) {
      if (!Array.isArray(ids) || !ids.length) return;
      copyClipBoard(ids.join('\n'), 'Danh sách ID');
    },

    formatDuration(startedAt, finishedAt) {
      if (!startedAt) return '—';
      const start = new Date(startedAt).getTime();
      const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
      const ms = Math.max(0, end - start);
      if (ms < 1000) return `${ms}ms`;
      const s = Math.floor(ms / 1000);
      if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60);
      const rem = s % 60;
      return `${m}m ${rem}s`;
    },

    runStatusLabel(s) {
      return { running: 'Đang chạy', done: 'Hoàn tất', crashed: 'Lỗi' }[s] || s;
    },

    runStatusClass(s) {
      return {
        running: 'label-primary',
        done: 'label-success',
        crashed: 'label-danger',
      }[s] || 'label-secondary';
    },

    summarizeIds(ids) {
      if (!Array.isArray(ids) || !ids.length) return '—';
      if (ids.length <= 3) return ids.join(', ');
      return `${ids.slice(0, 3).join(', ')} … (+${ids.length - 3})`;
    },

    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? text.slice(0, n) + '…' : text;
    },
  },
};
</script>

<style scoped>
pre {
  max-height: 200px;
  overflow: auto;
}
.ids-pre {
  max-height: 360px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
}
</style>
